/**
 * Détection des capacités du navigateur et choix de la stratégie d'écriture disque.
 *
 * ## Le problème que ce module résout
 *
 * Écrire un fichier reçu sur le disque n'est pas uniforme selon les navigateurs :
 *
 * - **Chromium** (Chrome, Edge, Opera) expose la File System Access API : on écrit en flux,
 *   sans limite de taille, avec un vrai curseur de progression.
 * - **Firefox et Safari** ne l'exposent pas. La seule issue sans dépendance est d'accumuler
 *   le fichier en mémoire. Au-delà de quelques centaines de mégaoctets, l'onglet meurt.
 *
 * ToffeeShare laisse l'utilisateur découvrir ce mur en plein transfert (« il s'arrête à
 * mi-chemin », dit leur FAQ). Ici, on **décide avant de commencer** et, si la capacité
 * manque, on refuse le transfert avec un message qui dit quoi faire.
 *
 * Refuser tôt est un choix : un échec à 60 % d'un fichier de 4 Go est bien plus coûteux
 * pour l'utilisateur qu'un refus immédiat et expliqué.
 */

import { MEMORY_WRITE_LIMIT, formatBytes } from '@attrape/shared';
import { msg, type MessageDescriptor } from '../i18n/index.js';
import { isStreamDownloadSupported } from './transfer/stream-download.js';

export type WriteStrategy = 'fs-access' | 'directory' | 'streamsaver' | 'memory';

export interface Capabilities {
  /** File System Access API : `showSaveFilePicker` disponible. */
  fileSystemAccess: boolean;
  /** Sélecteur de dossier : `showDirectoryPicker` disponible. */
  directoryPicker: boolean;
  /**
   * Téléchargement en flux par Service Worker (stratégie `streamsaver`).
   *
   * Vaut `false` sur Safari, qui expose pourtant toutes les API nécessaires mais ignore
   * la réponse du Service Worker dès qu'un `Content-Disposition: attachment` est présent.
   * Voir `docs/ADR-001`.
   */
  streamDownload: boolean;
  webRtc: boolean;
  webCrypto: boolean;
}

export interface StrategyDecision {
  strategy: WriteStrategy;
  /** `false` si le transfert doit être refusé avant même de commencer. */
  allowed: boolean;
  /**
   * Explication à afficher quand `allowed` vaut `false`.
   *
   * Un descripteur et non une phrase : ces messages contiennent la taille du fichier et
   * le nom du navigateur, et doivent rester traduisibles au moment de l'affichage.
   */
  refusalReason?: MessageDescriptor;
  /** Avertissement non bloquant (le transfert reste possible). */
  warning?: MessageDescriptor;
}

export function detectCapabilities(): Capabilities {
  const hasWindow = typeof window !== 'undefined';
  // `typeof === 'function'` et non `in` : une politique d'entreprise peut exposer la
  // propriété tout en la neutralisant (valeur `undefined` ou exception à l'appel).
  // `in` répondrait `true` dans ce cas et ferait choisir une stratégie inutilisable.
  return {
    fileSystemAccess: hasWindow && typeof window.showSaveFilePicker === 'function',
    directoryPicker: hasWindow && typeof window.showDirectoryPicker === 'function',
    streamDownload: hasWindow && isStreamDownloadSupported().supported,
    webRtc: typeof RTCPeerConnection !== 'undefined',
    webCrypto: typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined',
  };
}

function userAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent;
}

export function isSafari(): boolean {
  const agent = userAgent();
  // Chrome se déclare aussi « Safari » : il faut exclure Chromium explicitement.
  return /safari/i.test(agent) && !/chrome|chromium|crios|edg/i.test(agent);
}

/** Nom lisible du navigateur, utilisé dans les messages destinés à l'utilisateur. */
function browserName(): string {
  const agent = userAgent();
  if (/edg\//i.test(agent)) return 'Edge';
  if (/firefox/i.test(agent)) return 'Firefox';
  if (/chrome|crios/i.test(agent)) return 'Chrome';
  if (isSafari()) return 'Safari';
  return 'votre navigateur';
}

/**
 * Détermine la stratégie d'écriture pour un lot de fichiers.
 *
 * `largestFileBytes` — et non le total — est la contrainte en mode mémoire : les fichiers
 * sont écrits **séquentiellement**, donc le pic d'occupation est celui du plus gros fichier,
 * pas de la somme.
 */
export function decideWriteStrategy(
  fileCount: number,
  largestFileBytes: number,
  capabilities: Capabilities = detectCapabilities(),
): StrategyDecision {
  // Un seul fichier : un sélecteur, appelé pendant le clic de l'utilisateur, puis
  // écriture en flux sans limite de taille.
  if (capabilities.fileSystemAccess && fileCount === 1) {
    return { strategy: 'fs-access', allowed: true };
  }

  // Plusieurs fichiers : `showDirectoryPicker` fournit **une** autorisation qui couvre
  // ensuite la création de chaque fichier dans le dossier choisi.
  if (capabilities.fileSystemAccess && capabilities.directoryPicker && fileCount > 1) {
    return { strategy: 'directory', allowed: true };
  }

  // Repli mémoire. Il couvre le cas des navigateurs sans File System Access **et** celui
  // de Chromium sans sélecteur de dossier sur un lot de plusieurs fichiers.
  //
  // On ne tente surtout pas d'enchaîner un `showSaveFilePicker` par fichier :
  // l'autorisation exige une activation utilisateur transitoire, consommée par le premier
  // appel. Les suivants échoueraient en pleine réception, après que l'utilisateur a
  // déjà attendu.

  // Au-dessus de la limite mémoire, on tente le téléchargement en flux par Service
  // Worker. C'est le cas qui motive tout ce module : Firefox et Safari, gros fichiers.
  // Un lot de plusieurs fichiers reste en mémoire : le flux suppose une destination
  // unique, et enchaîner plusieurs téléchargements déclenchés par script est bloqué par
  // les navigateurs. On ne le promet donc pas.
  if (largestFileBytes > MEMORY_WRITE_LIMIT && fileCount === 1 && capabilities.streamDownload) {
    return { strategy: 'streamsaver', allowed: true };
  }

  if (largestFileBytes <= MEMORY_WRITE_LIMIT) {
    return {
      strategy: 'memory',
      allowed: true,
      warning: msg.key('errors.memoryWarning', { browser: browserName() }),
    };
  }

  return {
    strategy: 'memory',
    allowed: false,
    refusalReason: msg.key('errors.browserCannotReceive', {
      size: formatBytes(largestFileBytes),
      browser: browserName(),
      limit: formatBytes(MEMORY_WRITE_LIMIT),
    }),
  };
}
